 /**
 * DynamicPricingSystem
 * Système de tarification dynamique pour Shopify.
 * - Détecte automatiquement les produits compatibles (variantes au format "X à Yg = Z€/g" ou "Xg et + = Z€/g")
 * - Affiche le prix total en temps réel selon la quantité saisie
 * - Sélectionne automatiquement le bon variant selon la quantité
 * - Maintient la quantité saisie par l'utilisateur lors du changement de variant
 */

class DynamicPricingSystem {

  constructor() {
    this.productData = null;
    this.pricingTiers = [];
    this.quantityInput = null;
    this.priceDisplay = null;
    this.isCompatibleProduct = false;
    this.debugMode = false;

    // Flag : l'utilisateur est en train de saisir une quantité manuellement
    this.isUserTyping = false;
    this.userTypingTimeout = null;

    // Quantité cible à restaurer après changement programmatique de variant
    this.targetQuantity = null;

    this.init();
  }

  // ─────────────────────────────────────────────
  // UTILITAIRES
  // ─────────────────────────────────────────────

  log(message, data = null) {
    if (this.debugMode) {
      console.log('[DynamicPricing]', message, data ?? '');
    }
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  // ─────────────────────────────────────────────
  // INITIALISATION
  // ─────────────────────────────────────────────

  setup() {
    this.getProductData();

    if (!this.checkProductCompatibility()) {
      this.log('Produit non compatible — arrêt du système.');
      return;
    }

    this.isCompatibleProduct = true;
    document.body.classList.add('dynamic-pricing-enabled');
    this.log('Produit compatible détecté.');

    this.quantityInput = this.findQuantityInput();
    if (!this.quantityInput) {
      this.log('ERREUR : input quantité introuvable.');
      return;
    }

    this.parseVariants();
    this.createPriceDisplay();
    this.setupEventListeners();

    // Synchronisation initiale avec un léger délai pour laisser Shopify finir son rendu
    setTimeout(() => this.updatePrice(), 300);
  }

  // ─────────────────────────────────────────────
  // DONNÉES PRODUIT
  // ─────────────────────────────────────────────

  getProductData() {
    // Méthode 1 : variable globale Shopify
    if (window.product?.variants?.length) {
      this.productData = window.product;
      this.log('Données produit récupérées depuis window.product');
      return;
    }

    // Méthode 2 : balise <script type="application/json">
    for (const script of document.querySelectorAll('script[type="application/json"]')) {
      try {
        const data = JSON.parse(script.textContent);
        if (Array.isArray(data.variants) && data.variants.length) {
          this.productData = data;
          this.log('Données produit récupérées depuis script JSON');
          return;
        }
      } catch {
        // script non pertinent, on continue
      }
    }

    // Méthode 3 : parsing du formulaire HTML
    this.getProductDataFromForm();
  }

  getProductDataFromForm() {
    const variants = [];

    document.querySelectorAll('select[name="id"] option, input[name="id"]').forEach(el => {
      const id = el.value;
      const title = (el.textContent || el.dataset.title || '').trim();
      if (id && title) {
        variants.push({
          id,
          title,
          price: this.extractPriceFromText(title),
        });
      }
    });

    if (variants.length) {
      this.productData = { variants };
      this.log('Données produit récupérées depuis le formulaire HTML', variants);
    }
  }

  extractPriceFromText(text) {
    const match = text.match(/(\d+[,.]?\d*)\s*€/);
    return match ? parseFloat(match[1].replace(',', '.')) * 100 : 0;
  }

  // ─────────────────────────────────────────────
  // COMPATIBILITÉ
  // ─────────────────────────────────────────────

  checkProductCompatibility() {
    if (!this.productData?.variants?.length) {
      this.log('Aucune donnée produit ou aucun variant.');
      return false;
    }

    const total = this.productData.variants.length;
    const compatible = this.productData.variants.filter(v => this.isVariantCompatible(v.title)).length;

    this.log(`${compatible}/${total} variants compatibles`);

    return compatible / total >= 0.7 || compatible >= 2;
  }

  isVariantCompatible(title) {
    return (
      /^(\d+)\s*à\s*(\d+)g\s*=\s*(\d+(?:[,.]\d+)?)€\/g$/i.test(title) ||
      /^(\d+)g\s*et\s*\+\s*=\s*(\d+(?:[,.]\d+)?)€\/g$/i.test(title)
    );
  }

  // ─────────────────────────────────────────────
  // PARSING DES VARIANTS → PALIERS
  // ─────────────────────────────────────────────

  parseVariants() {
    this.pricingTiers = [];

    for (const variant of this.productData.variants) {
      const tier = this.parseVariantTitle(variant.title);
      if (tier) {
        this.pricingTiers.push({
          ...tier,
          variantId: variant.id,
          basePrice: variant.price,
        });
      }
    }

    // Trier par quantité minimum croissante
    this.pricingTiers.sort((a, b) => a.minQty - b.minQty);
    this.log('Paliers de prix:', this.pricingTiers);
  }

  parseVariantTitle(title) {
    // Format "X à Yg = Z€/g"
    const m1 = title.match(/^(\d+)\s*à\s*(\d+)g\s*=\s*(\d+(?:[,.]\d+)?)€\/g$/i);
    if (m1) {
      return {
        minQty: parseInt(m1[1]),
        maxQty: parseInt(m1[2]),
        pricePerUnit: parseFloat(m1[3].replace(',', '.')),
      };
    }

    // Format "Xg et + = Z€/g"
    const m2 = title.match(/^(\d+)g\s*et\s*\+\s*=\s*(\d+(?:[,.]\d+)?)€\/g$/i);
    if (m2) {
      return {
        minQty: parseInt(m2[1]),
        maxQty: Infinity,
        pricePerUnit: parseFloat(m2[2].replace(',', '.')),
      };
    }

    return null;
  }

  // ─────────────────────────────────────────────
  // DOM
  // ─────────────────────────────────────────────

  findQuantityInput() {
    const selectors = [
      'input[name="quantity"]',
      'input[id*="quantity"]',
      'input[class*="quantity"]',
      '.quantity-input input',
      '.product-form__quantity input',
      '[data-quantity-input]',
      '#Quantity-sticky-atc-bar',
    ];

    for (const selector of selectors) {
      const input = document.querySelector(selector);
      if (input) {
        this.log(`Input quantité trouvé : ${selector}`);
        return input;
      }
    }

    return null;
  }

  createPriceDisplay() {
    // Ne pas créer deux fois
    const existing = document.getElementById('dynamic-total-price');
    if (existing) {
      this.priceDisplay = existing;
      return;
    }

    const span = document.createElement('span');
    span.id = 'dynamic-total-price';
    span.className = 'dynamic-price-display';
    span.textContent = '0 €';

    const wrapper = document.createElement('div');
    wrapper.className = 'dynamic-price-wrapper';
    wrapper.appendChild(span);

    const insertAfter =
      this.quantityInput.closest('.field') ||
      this.quantityInput.closest('.product-form__input') ||
      this.quantityInput.closest('.quantity') ||
      this.quantityInput.parentNode;

    insertAfter.insertAdjacentElement('afterend', wrapper);
    this.priceDisplay = span;

    this.log("Élément d'affichage du prix créé.");
  }

  // ─────────────────────────────────────────────
  // CALCUL DU PRIX
  // ─────────────────────────────────────────────

  getPriceForQuantity(quantity) {
    if (!this.pricingTiers.length || quantity <= 0) return 0;

    const tier = this.pricingTiers.find(t => quantity >= t.minQty && quantity <= t.maxQty);

    if (tier) return tier.pricePerUnit;

    // Fallback : dernier palier (quantité très élevée)
    return this.pricingTiers[this.pricingTiers.length - 1].pricePerUnit;
  }

  updatePrice() {
    if (!this.priceDisplay || !this.isCompatibleProduct) return;

    const quantity = parseInt(this.quantityInput.value) || 0;

    if (quantity <= 0) {
      this.priceDisplay.textContent = '0 €';
      return;
    }

    const pricePerUnit = this.getPriceForQuantity(quantity);
    const total = quantity * pricePerUnit;

    this.priceDisplay.textContent = this.formatPrice(total);
    this.log(`Quantité: ${quantity}g | ${pricePerUnit}€/g | Total: ${total}€`);
  }

  formatPrice(price) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  }

  // ─────────────────────────────────────────────
  // SÉLECTION DE VARIANT
  // ─────────────────────────────────────────────

  getSelectedVariant() {
    const inputs = document.querySelectorAll(
      'input[name="id"]:checked, select[name="id"], .sticky-atc-bar__variant-select'
    );

    for (const input of inputs) {
      const id = parseInt(input.value);
      if (!isNaN(id)) {
        return this.productData.variants.find(v => parseInt(v.id) === id) || null;
      }
    }

    return null;
  }

  /**
   * Sélectionne le variant correspondant à la quantité donnée.
   * Restaure ensuite la quantité cible pour éviter qu'elle soit écrasée
   * par le handler de changement de variant.
   */
  selectVariantForQuantity(quantity) {
    const rounded = Math.round(quantity);

    const matchingTier = this.pricingTiers.find(
      t => rounded >= t.minQty && rounded <= t.maxQty
    );

    if (!matchingTier) {
      this.log(`Aucun palier pour ${rounded}g`);
      return;
    }

    const variant = this.productData.variants.find(v => {
      const t = this.parseVariantTitle(v.title);
      return t && t.minQty === matchingTier.minQty && t.maxQty === matchingTier.maxQty;
    });

    if (!variant) {
      this.log(`Aucun variant pour le palier ${matchingTier.minQty}-${matchingTier.maxQty}`);
      return;
    }

    const variantId = variant.id.toString();
    const current = this.getSelectedVariant();

    if (current?.id.toString() === variantId) {
      this.log(`Variant ${variantId} déjà sélectionné.`);
      return;
    }

    this.log(`Sélection du variant ${variantId} pour ${rounded}g`);

    // Mémoriser la quantité cible avant de dispatcher l'event
    this.targetQuantity = quantity;

    // Sélection selon le type de contrôle présent dans le thème
    const select =
      document.querySelector('select[name="id"]') ||
      document.querySelector('.sticky-atc-bar__variant-select');

    if (select) {
      select.value = variantId;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      this.restoreQuantityAfterVariantChange();
      return;
    }

    const radio = document.querySelector(`input[type="radio"][name="id"][value="${variantId}"]`);
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      this.restoreQuantityAfterVariantChange();
      return;
    }

    const button = document.querySelector(`[data-variant-id="${variantId}"]`);
    if (button) {
      button.click();
      this.restoreQuantityAfterVariantChange();
    }
  }

  /**
   * Restaure la quantité cible après que le thème Shopify
   * ait potentiellement réinitialisé l'input lors du changement de variant.
   * Double restauration : synchrone + asynchrone (50ms) pour couvrir
   * les thèmes qui réinitialisent la quantité de manière asynchrone.
   */
  restoreQuantityAfterVariantChange() {
    if (this.targetQuantity === null) return;

    const qty = this.targetQuantity;

    // Restauration immédiate
    this.quantityInput.value = qty;

    // Restauration différée pour les thèmes asynchrones
    setTimeout(() => {
      this.quantityInput.value = qty;
      this.targetQuantity = null;
      this.updatePrice();
    }, 50);
  }

  // ─────────────────────────────────────────────
  // GESTION DU CHANGEMENT DE VARIANT MANUEL
  // ─────────────────────────────────────────────

  /**
   * Appelé quand l'utilisateur change le variant manuellement.
   * Ajuste la quantité si elle est inférieure au minimum du palier sélectionné.
   */
  onManualVariantChange() {
    // Ignorer si une restauration de quantité est en cours (changement programmatique)
    if (this.targetQuantity !== null) {
      this.log('onManualVariantChange ignoré (restauration de quantité en cours).');
      return;
    }

    // Ignorer si l'utilisateur est en train de taper une quantité
    if (this.isUserTyping) {
      this.log('onManualVariantChange ignoré (saisie de quantité en cours).');
      return;
    }

    const selected = this.getSelectedVariant();
    if (!selected) return;

    const tier = this.parseVariantTitle(selected.title);
    if (!tier) return;

    const currentQty = parseInt(this.quantityInput.value) || 0;

    if (currentQty < tier.minQty) {
      this.log(`Quantité (${currentQty}g) < minQty (${tier.minQty}g) → ajustement.`);
      this.quantityInput.value = tier.minQty;
    }

    this.updatePrice();
  }

  // ─────────────────────────────────────────────
  // GESTION DE LA SAISIE DE QUANTITÉ
  // ─────────────────────────────────────────────

  /**
   * Appelé à chaque frappe dans l'input quantité.
   * Met à jour le prix immédiatement et planifie la sélection du bon variant
   * après 600ms d'inactivité.
   */
  onQuantityInput() {
    this.isUserTyping = true;
    this.updatePrice();

    clearTimeout(this.userTypingTimeout);
    this.userTypingTimeout = setTimeout(() => {
      this.isUserTyping = false;
      const quantity = parseInt(this.quantityInput.value) || 0;
      if (quantity > 0) {
        this.selectVariantForQuantity(quantity);
      }
    }, 600);
  }

  /**
   * Appelé quand la valeur est confirmée (blur, change, boutons +/-).
   * Déclenche immédiatement la sélection du variant sans délai.
   */
  onQuantityCommit() {
    clearTimeout(this.userTypingTimeout);
    this.isUserTyping = false;

    const quantity = parseInt(this.quantityInput.value) || 0;
    if (quantity > 0) {
      this.selectVariantForQuantity(quantity);
    }

    this.updatePrice();
  }

  // ─────────────────────────────────────────────
  // EVENT LISTENERS
  // ─────────────────────────────────────────────

  setupEventListeners() {
    // ── Changements de variant (sélection manuelle par l'utilisateur) ──
    const onVariantChange = () => {
      // setTimeout 0 pour laisser le DOM se mettre à jour avant de lire la valeur
      setTimeout(() => this.onManualVariantChange(), 0);
    };

    const variantSelect = document.querySelector('select[name="id"]');
    if (variantSelect) {
      variantSelect.addEventListener('change', onVariantChange);
    }

    document.querySelectorAll('input[name="id"]').forEach(input => {
      input.addEventListener('change', onVariantChange);
    });

    const stickySelect = document.querySelector('.sticky-atc-bar__variant-select');
    if (stickySelect) {
      stickySelect.addEventListener('change', onVariantChange);
    }

    // ── Saisie de quantité ──
    if (this.quantityInput) {
      // Pendant la frappe : mise à jour du prix + débounce pour la sélection du variant
      this.quantityInput.addEventListener('input', () => this.onQuantityInput());

      // À la confirmation (Tab, blur, Enter) : sélection immédiate du variant
      this.quantityInput.addEventListener('change', () => this.onQuantityCommit());
    }

    // ── Boutons +/- personnalisés du thème ──
    // On attend 50ms pour que le thème ait mis à jour la valeur de l'input
    document.querySelectorAll(
      '[data-quantity-wrapper] button, [name="plus"], [name="minus"]'
    ).forEach(button => {
      button.addEventListener('click', () => {
        setTimeout(() => this.onQuantityCommit(), 50);
      });
    });

    // ── Polling léger : rattrape les changements faits par des scripts tiers ──
    setInterval(() => {
      if (!this.isUserTyping && this.targetQuantity === null) {
        this.updatePrice();
      }
    }, 500);

    this.log('Event listeners configurés.');
  }
}

// ─────────────────────────────────────────────
// STYLES CSS
// ─────────────────────────────────────────────

const DYNAMIC_PRICING_CSS = `
  .product-form__quantity {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
  }

  .product-form__quantity > .quantity__label {
    flex-basis: 100%;
    margin-bottom: 0.5rem;
  }

  .product-form__quantity > quantity-input,
  .product-form__quantity > .dynamic-price-wrapper {
    flex-shrink: 0;
    flex-grow: 0;
  }

  .dynamic-price-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.5rem 1rem;
    background-color: #0F4131;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    min-width: 120px;
    animation: dp-fadeInUp 0.4s ease-out;
  }

  .dynamic-price-display {
    font-size: 1.8rem;
    font-weight: 400;
    color: #ffffff;
    text-align: center;
    white-space: nowrap;
  }

  @media (max-width: 767px) {
    .product-form__quantity {
      justify-content: center;
    }
  }

  @keyframes dp-fadeInUp {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

function injectStyles() {
  if (document.getElementById('dynamic-pricing-styles')) return;
  const style = document.createElement('style');
  style.id = 'dynamic-pricing-styles';
  style.textContent = DYNAMIC_PRICING_CSS;
  document.head.appendChild(style);
}

// ─────────────────────────────────────────────
// POINT D'ENTRÉE
// ─────────────────────────────────────────────

function initDynamicPricing() {
  // Uniquement sur les pages produit
  const isProductPage =
    document.body.classList.contains('template-product') ||
    document.querySelector('.product-form') ||
    document.querySelector('[data-product-form]') ||
    window.location.pathname.includes('/products/');

  if (!isProductPage) {
    console.log('[DynamicPricing] Pas sur une page produit — arrêt.');
    return;
  }

  injectStyles();

  setTimeout(() => {
    window.dynamicPricingSystem = new DynamicPricingSystem();
  }, 300);
}

// Initialisation robuste : couvre DOMContentLoaded, déjà chargé, et load tardif
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDynamicPricing);
} else {
  initDynamicPricing();
}

window.addEventListener('load', () => {
  if (!window.dynamicPricingSystem) {
    initDynamicPricing();
  }
});
